import debounce from 'lodash/debounce'
import React, { useEffect } from 'react'

/**
 * Find the height of the HTMLTextAreaElement
 */
const getElemHeight = (
  inputRef: React.RefObject<HTMLTextAreaElement>,
  min: number,
  max: number
) => {
  const element = inputRef.current
  if (!element) {
    return
  }
  // If we don't set the height to 0px, scrollHeight will not match the
  // content of the text area.
  // https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollHeight
  // This is going to be set to right side at the end of this function.
  element.style.height = '0px'
  const computedStyle = getComputedStyle(element)

  const scrollHeight = element.scrollHeight
  const paddingTop = parseInt(computedStyle.paddingTop, 10)
  const paddingBottom = parseInt(computedStyle.paddingBottom, 10)
  const lineHeight = parseInt(computedStyle.lineHeight, 10)

  const totalHeight = scrollHeight - paddingBottom - paddingTop
  const totalContentLines = Math.round(totalHeight / lineHeight)

  let targetLines = min

  if (totalContentLines > max) {
    targetLines = max
  } else if (totalContentLines <= max && totalContentLines > min) {
    targetLines = totalContentLines
  }

  const height = `${paddingTop + paddingBottom + lineHeight * targetLines}px`
  element.style.height = height
}

const debouncedGetHeight = debounce(getElemHeight, 250)

/**
 * Allows a textarea to grow as new text is entered.
 * Allows a min and max number of lines to be set, defaulting to a min height of 2 lines.
 */
export const useAutoGrowTextArea = (
  inputRef: React.RefObject<HTMLTextAreaElement>,
  min: number = 3,
  max: number = 5
) => {
  useEffect(() => {
    debouncedGetHeight(inputRef, min, max)
  }, [inputRef, min, max, inputRef.current?.value])
}
